import { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_SCOPE } from 'components/invoiceList/useInvoiceFilters';
import ApiDatabase from 'server';

export const REMINDERS_PAGE_SIZE = 20;

/**
 * One tab of the reminders (JBWEB-1283), one page at a time, searched as the
 * invoice list is (`q`, `scope`, JBWEB-1270). A new tab or search is page 1.
 * Only the read asked last may answer.
 *
 * `refreshKey` changing reads the page again quietly: the rows stay on screen
 * while it is read (after "Tout relancer"). `updateItem` patches a row in
 * place (after a suspension), without reading.
 */
const useInvoiceReminderList = ({
  tab,
  q = '',
  scope = DEFAULT_SCOPE,
  refreshKey = 0,
}) => {
  // The scope only says where the text is looked for: with no text it asks for nothing.
  const askedScope = q ? scope : DEFAULT_SCOPE;
  const listKey = JSON.stringify([tab, q, askedScope]);

  const [view, setView] = useState({ listKey, page: 1 });
  if (view.listKey !== listKey) setView({ listKey, page: 1 });
  const page = view.listKey === listKey ? view.page : 1;

  const [outcome, setOutcome] = useState(null);
  const answer =
    outcome?.listKey === listKey && outcome.page === page ? outcome : null;
  const list = answer?.list ?? null;
  const failed = Boolean(answer?.failed);

  const latestRead = useRef(0);

  const read = useCallback(
    (isQuiet) => {
      latestRead.current += 1;
      const readId = latestRead.current;
      if (!isQuiet) setOutcome(null);
      ApiDatabase.getInvoiceReminders(
        {
          tab,
          page,
          limit: REMINDERS_PAGE_SIZE,
          ...(q && { q, scope: askedScope }),
        },
        (data) => {
          if (readId === latestRead.current)
            setOutcome({ listKey, page, list: data });
        },
        (error) => {
          if (readId !== latestRead.current || error?.name === 'CanceledError')
            return;
          // A quiet read that fails leaves the rows as they were.
          if (!isQuiet) setOutcome({ listKey, page, failed: true });
        },
      );
    },
    [tab, q, askedScope, listKey, page],
  );

  useEffect(() => {
    read(false);
    return () => {
      // A read still on its way for a tab, search or page left must not answer.
      latestRead.current += 1;
    };
  }, [read]);

  // Through a ref: only a new key reads again, not a new list nor a new page.
  const refreshRead = useRef(null);
  refreshRead.current = () => read(Boolean(list));
  const lastRefreshKey = useRef(refreshKey);
  useEffect(() => {
    if (lastRefreshKey.current === refreshKey) return;
    lastRefreshKey.current = refreshKey;
    refreshRead.current();
  }, [refreshKey]);

  // Rows gone since (sent, settled) can leave a page past the last one.
  useEffect(() => {
    if (list && list.pages >= 1 && page > list.pages) {
      setView({ listKey, page: list.pages });
    }
  }, [list, page, listKey]);

  const setPage = useCallback(
    (nextPage) => setView({ listKey, page: nextPage }),
    [listKey],
  );

  const reload = useCallback(() => read(Boolean(list)), [read, list]);

  const updateItem = useCallback((number, patch) => {
    setOutcome((current) =>
      current?.list
        ? {
            ...current,
            list: {
              ...current.list,
              items: (current.list.items ?? []).map((item) =>
                item.number === number ? { ...item, ...patch } : item,
              ),
            },
          }
        : current,
    );
  }, []);

  return {
    listKey,
    list,
    isLoading: !list && !failed,
    failed,
    page,
    setPage,
    reload,
    updateItem,
    isFiltered: Boolean(q),
  };
};

export default useInvoiceReminderList;
